import { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataContext } from 'providers/data';

import { Box, Grid } from '@mui/material';
import GlobalStyles from "@mui/material/GlobalStyles";
import FadeAnimation from 'components/views/FadeAnimation';

import Loading from 'components/views/RegisterValidationComp/Loading';
import AddressForm from 'components/views/RegisterValidationComp/StepsForm/AddressForm';
import ValidateToken from 'components/views/RegisterValidationComp/StepsForm/ValidateToken';
import BannerSideBar from 'components/views/RegisterValidationComp/StepsForm/BannerSideBar';
import HeaderSections from 'components/views/RegisterValidationComp/StepsForm/HeaderSections';
import PersonalDataForm from 'components/views/RegisterValidationComp/StepsForm/PersonalDataForm';
import ErrorGeneral from 'components/views/RegisterValidationComp/PageSuccessAndErrors/ErrorGeneral';
import BirthDateError from 'components/views/RegisterValidationComp/PageSuccessAndErrors/BirthDateError';
import ConfirmedToken from 'components/views/RegisterValidationComp/PageSuccessAndErrors/ConfirmedToken';
import InstallationDone from 'components/views/RegisterValidationComp/PageSuccessAndErrors/InstallationDone';
import { ModalConfirmation, ModalError, ModalDefault, ModalZipCodeOvernight } from 'components/views/RegisterValidationComp/ModalRegisterValidation';

import { ContainerForm } from './styles';

const RegisterValidation = () => {
    const [searchParams] = useSearchParams();

    const [stepNav, setStepNav] = useState(0);
    const [isError, setIsError] = useState({ isError: false, isData: false });
    const [showModalError, setShowModalError] = useState(false);
    const [showModalErrorDefault, setShowModalErrorDefault] = useState(false);
    const [showModalConfirmation, setShowModalConfirmation] = useState(false);
    const [showModalZipCodeOvernight, setShowModalZipCodeOvernight] = useState(false);

    const person = searchParams.get('person');
    const schedule = searchParams.get('schedule');

    const { dataSearchPersonData, dataTokenExternal, dataValidatePersonalData, handleGetSearchPersonData, handlePostAuthenticationClientCredentials } = useContext(DataContext);

    const STEPS = [
        <AddressForm pushProgressStep={() => setShowModalZipCodeOvernight(true)} />,
        <PersonalDataForm pushProgressStep={() => setShowModalConfirmation(true)} />,
        <ValidateToken pushProgressStep={(step) => setStepNav(step)} />,
        <ConfirmedToken />,
        <InstallationDone />
    ];

    const getStepComponent = (stepActive: number): JSX.Element => {
        return STEPS[stepActive] || STEPS[0];
    };

    useEffect(() => {
        const tokenExternal = localStorage.getItem('token_external');
        const expiration = Number(localStorage.getItem('expires_external'));

        if ((!tokenExternal && typeof dataTokenExternal === 'undefined') || (Date.now() > Number(expiration))) {
            handlePostAuthenticationClientCredentials();
            return;
        }

        if (typeof dataSearchPersonData === 'undefined' && tokenExternal && person && schedule) {
            handleGetSearchPersonData({
                person,
                schedule,
                token: tokenExternal
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataTokenExternal]);

    useEffect(() => {
        if ((dataSearchPersonData === false) || (dataSearchPersonData === null) || (dataTokenExternal === false)) {
            setIsError({ isError: true, isData: dataSearchPersonData === null });
            return;
        }

        setIsError({ isError: false, isData: false });

        if (dataSearchPersonData) {
            if (dataSearchPersonData.FL_AGENDAMENTO_FINALIZADO) {
                setStepNav(4);
                return;
            }

            if (dataSearchPersonData.FL_VALIDACAO_REALIZADA) {
                setStepNav(3);
                return;
            }
        }
    }, [dataSearchPersonData, dataTokenExternal]);

    useEffect(() => {
        if (dataValidatePersonalData === false) {
            setShowModalErrorDefault(true);
            return;
        }

        if (dataValidatePersonalData === null) {
            setShowModalError(true);
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataValidatePersonalData]);

    return (
        <FadeAnimation>
            <Box display='flex' width='100%' height='100vh' sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                <GlobalStyles
                    styles={{
                        'iframe[title="Mensagem da empresa"]': {
                            display: 'none !important',
                            opacity: '0 !important'
                        },
                        'iframe[title = "Fechar mensagem"]': {
                            display: 'none !important',
                            opacity: '0 !important'
                        },
                        'iframe[title="Close message"]': {
                            display: 'none !important',
                            opacity: '0 !important'
                        },
                        'iframe[title="Company message"]': {
                            display: 'none !important',
                            opacity: '0 !important'
                        },
                        'iframe[title="Message from company"]': {
                            display: 'none !important',
                            opacity: '0 !important'
                        },
                        '#launcher': {
                            display: 'none'
                        }
                    }}
                />
                {typeof dataSearchPersonData === 'undefined' ?
                    <Loading />
                    :
                    isError.isError ? (
                        isError.isData ?
                            <BirthDateError />
                            :
                            <ErrorGeneral />
                    )
                        :
                        (stepNav < 3) ?
                            <>
                                <BannerSideBar />
                                <ContainerForm>
                                    <HeaderSections step={stepNav} />
                                    <Grid width='100%' maxWidth='27.2rem' height='fit-content' justifySelf='center' sx={{
                                        alignContent: { xs: 'flex-start', md: 'center' },
                                        paddingTop: { md: '9rem', lg: '8rem' },
                                        paddingBottom: { xs: '7rem', md: '2rem' }
                                    }}>
                                        {getStepComponent(stepNav)}
                                    </Grid>
                                </ContainerForm>
                            </>
                            :
                            getStepComponent(stepNav)
                }

                <ModalConfirmation open={showModalConfirmation} handleClick={(e) => { setShowModalConfirmation(false); e && setStepNav(2); }} />
                <ModalError open={showModalError} handleClick={() => setShowModalError(false)} />
                <ModalDefault open={showModalErrorDefault} handleClick={() => setShowModalErrorDefault(false)} />
                <ModalZipCodeOvernight open={showModalZipCodeOvernight} handleClick={(e) => { setShowModalZipCodeOvernight(false); setStepNav(1); }} />
            </Box>
        </FadeAnimation>
    );
}

export default RegisterValidation;